import Link from 'components/ui/AppLink';
import Loader from 'components/ui/LoadingIndicator';
import ContainerBox from 'components/ui/ContainerBox';
import UserInterfaceCheck from '@mds/icons/Check';
import {
    Box,
    Breadcrumbs,
    Tooltip,
    Typography,
    Typography as BreadcrumbsText,
} from '@mui/material';
import { Snackbar } from 'components/ui/Feedback';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { Grid } from '@mui/material';
import {
    ColDef,
    GridApi,
    ICellRendererParams,
    IRowNode,
    NewColumnsLoadedEvent,
} from 'ag-grid-community';
import AdminList, {
    DecisionCellRenderer,
    LinkCellRenderer,
    ReadOnlyLabelCellRenderer,
} from '../../ui/admin/AdminList';
import {
    configureLongDateFormat,
    formatDisplayDateTime,
    formatDisplayDate,
    dateComparator,
} from '../../../utils/date.utils';
import {
    formatPlural,
    handleEntityPluralCase,
} from '../../../utils/string.utils';
import { baseUrls } from '../../../utils/baseUrls.utils';
import {
    GET_PREMIUM_CHANNEL_SUBSCRIBED_USERS_BY_ID,
    PROCESS_FREE_CLOSED_REQUEST,
    PROCESS_TRIAL_REQUEST,
} from '../../../gql/channels';
import {
    Channel,
    ChannelState,
    ChannelSubscription,
    ChannelSubscriptionType,
    ChannelTypes,
    User,
} from '../../../shared/types';
import { ChannelSubscribersFilter } from '../../ui/admin/ChannelSubscribersFilter';
import {
    ChannelSubscriberKind,
    ResearchRole,
} from '../../../shared/global-enums';
import {
    formatChannelSubscriptionType,
    isUserNotChannelAdmin,
} from '../../../utils/channel.utils';
import {
    isNonChargeChannelSubscriptionType,
    isSubscriptionActive,
    isSubscriptionExpired,
    isStripeChannelSubscriptionType,
    isAccountChannelSubscriptionType,
    isTrialChannelSubscriptionType,
    isPaidSubscription,
    isRebatedAccountSubscription,
    isActivePaidSubscription,
} from '../../../utils/channelSubscriptions.utils';
import { useAuth0Data } from 'neon-auth0';
import jwt from 'jwt-decode';
import { config } from '../../../config';
import { useUser } from '../../providers/UserProvider';
import SubscriberStatsPanel from './SubscriberStatsPanel';
import { MessageTypes } from '../../providers/NotificationProvider';
import { USER_ARTICLE_VIEWS_BY_CHANNEL } from '../../../gql/users';
import { TextOverlapping } from '../../ui/TextOverlapping';
import { formatAnonymousUserData } from '../../../utils/utils';
import { ChannelLabel } from '../../ui/ChannelLabel';

import {
    useApolloClient,
    useLazyQuery,
    useMutation,
} from '@apollo/react-hooks';
import { useHistory, useParams } from 'react-router-dom';
configureLongDateFormat();

interface PremiumChannelsStatsProps {
    channel: Channel;
}

const SubscriberCategoriesStatsBreadcrumbs: FC<PremiumChannelsStatsProps> = ({
    channel,
}) => (
    <Breadcrumbs>
        <Link to={baseUrls.publisher.premiumChannels.list}>Channels</Link>
        <Link
            to={`${baseUrls.publisher.premiumChannels.list}/edit/${channel._id}`}
        >
            {channel.name}
        </Link>
        <BreadcrumbsText>Subscriber stats</BreadcrumbsText>
    </Breadcrumbs>
);

const getNodeValue = (node: IRowNode, field: string) =>
    node.data?.channelSubscriptions[0]?.[field as any as string];

const createSubscriptionDateComparator =
    (field: 'createdAt' | 'updatedAt' | 'cancelAt' | 'cancelRequestedAt') =>
    (_a: any, _b: any, nodeA: IRowNode, nodeB: IRowNode) => {
        const date1 = getNodeValue(nodeA, field);
        const date2 = getNodeValue(nodeB, field);
        return dateComparator(date1, date2);
    };

const ChannelsStatsPage: FC = () => {
    const me = useUser();
    const { hasScope } = me;
    const { _id } = useParams<{ _id: string }>();
    const history = useHistory();
    const [subscriberFilter, setSubscriberFilter] =
        useState<ChannelSubscriberKind>(ChannelSubscriberKind.Trial);
    const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
    const [approveTrialAlertBox, setApproveTrialAlertBox] = useState<{
        visible: boolean;
        channelTrialDuration?: number;
    }>({
        visible: false,
    });
    const [rejectTrialAlertBoxVisible, setRejectTrialAlertBoxVisible] =
        useState<boolean>(false);
    const [acceptAlertBox, setAcceptAlertBox] = useState({
        visible: false,
        username: '',
    });
    const [rejectAlertBox, setRejectAlertBox] = useState({
        visible: false,
        username: '',
    });
    const [queryString, setQueryString] = useState<string>('');
    const { accessToken } = useAuth0Data();
    let neonUserId = me?.userDetails?.id ?? '';
    if (!neonUserId && accessToken) {
        neonUserId =
            jwt<Record<string, string | undefined>>(accessToken)?.[
                config.auth0.namespace + 'neonUserId'
            ] ?? '';
    }
    const isInsightsAdmin = [ResearchRole.Administrator].some(hasScope);
    const channelAdmin = [ResearchRole.Administrator, ResearchRole.Editor].some(
        hasScope
    );

    const canBeDeleted = useMemo(
        () => (channelSubscription: ChannelSubscription) => {
            if (subscriberFilter === ChannelSubscriberKind.Unsubscribed)
                return false;
            return (
                (!isStripeChannelSubscriptionType(
                    channelSubscription.subscriptionType
                ) &&
                    isSubscriptionActive(channelSubscription)) ||
                (isSubscriptionExpired(channelSubscription) &&
                    isTrialChannelSubscriptionType(
                        channelSubscription.subscriptionType
                    ))
            );
        },
        [subscriberFilter]
    );

    const [findChannel, { data, loading }] = useLazyQuery<{ channel: Channel }>(
        GET_PREMIUM_CHANNEL_SUBSCRIBED_USERS_BY_ID,
        {
            fetchPolicy: 'network-only',
            variables: { id: _id },
        }
    );

    const isFreeChannelSubscriptionType =
        data?.channel?.type !== ChannelTypes.premium;

    const client = useApolloClient();
    const [activeTrialUsersWithViewCount, setActiveTrialUsersWithViewCount] =
        useState<{ [id: string]: number }>({});

    const [processTrialRequest, { loading: processTrialRequestLoading }] =
        useMutation(PROCESS_TRIAL_REQUEST, {
            onCompleted: (data) => {
                findChannel();
            },
        });

    const [
        processFreeClosedRequest,
        { loading: processFreeClosedRequestLoading },
    ] = useMutation(PROCESS_FREE_CLOSED_REQUEST, {
        onCompleted: (data) => {
            findChannel();
        },
    });

    const activeChannelSubscription = useMemo(
        () => (cs: ChannelSubscription) =>
            cs.channelId === data?.channel._id &&
            cs.subscriptionState === 'active' &&
            cs.subscriptionType === ChannelSubscriptionType.Trial,
        [data?.channel]
    );
    const isActiveTrialUser = (user: User) =>
        user.channelSubscriptions.some(activeChannelSubscription);

    const makeEditSubscriberURL = (
        channelId: string,
        neonUserId: any
    ): string =>
        `${baseUrls.publisher.premiumChannels.subscriberMain}/${channelId}/edit/${encodeURIComponent(neonUserId)}`;

    const gridCallback = (api: GridApi) => {
        setGridApi(api);
    };

    useEffect(() => {
        if (data?.channel?.state === ChannelState.Archived) {
            history.push(baseUrls.publisher.premiumChannels.list);
        }
    }, [data?.channel?.state, history]);

    useEffect(() => {
        if (_id) {
            findChannel();
        }
    }, [_id, findChannel]);

    useEffect(() => {
        if (isInsightsAdmin) return;

        if (
            data &&
            isUserNotChannelAdmin(data.channel.adminUsers, neonUserId)
        ) {
            history.push(baseUrls.publisher.premiumChannels.list);
        }
    }, [data, history, isInsightsAdmin, neonUserId]);

    useEffect(() => {
        if (!gridApi) return;
        gridApi.setGridOption('quickFilterText', queryString);
    }, [gridApi, queryString]);

    useEffect(() => {
        const trialUsers =
            data?.channel.channelSubscribedUsers.filter(isActiveTrialUser);
        if (!trialUsers?.length) return;
        Promise.all(
            trialUsers.map((u) =>
                client.query({
                    query: USER_ARTICLE_VIEWS_BY_CHANNEL,
                    variables: { id: u._id, channelId: data?.channel._id },
                })
            )
        ).then((viewsData) => {
            const map = viewsData.reduce(
                (res, view) => ({
                    ...res,
                    [view.data.user._id]: view.data.user.articleViewsByChannel,
                }),
                {}
            );
            setActiveTrialUsersWithViewCount(map);
        });
    }, [data?.channel.channelSubscribedUsers]);

    useEffect(() => {
        if (data?.channel) {
            setSubscriberFilter(
                data.channel.type === ChannelTypes.premium
                    ? ChannelSubscriberKind.Trial
                    : ChannelSubscriberKind.All
            );
        }
    }, [data]);

    const columnDefs: ColDef[] = useMemo(
        () => [
            {
                headerName: 'User details',
                field: 'name',
                width: 400,
                menuTabs: [],
                cellRenderer: (props: any) => {
                    const value = formatAnonymousUserData(props.value);
                    return channelAdmin &&
                        canBeDeleted(props.data.channelSubscriptions[0]) ? (
                        <LinkCellRenderer
                            history={history}
                            label={value}
                            url={makeEditSubscriberURL(_id, props.data._id)}
                        />
                    ) : (
                        <ReadOnlyLabelCellRenderer {...props} value={value} />
                    );
                },
            },
            {
                headerName: 'Company',
                field: 'company',
                cellRenderer: (props: { value: string }) => props.value ?? '',
                menuTabs: [],
            },
            {
                headerName: 'Email',
                field: 'email',
                width: 300,
                cellRenderer: (props: { value: string }) => (
                    <TextOverlapping
                        value={formatAnonymousUserData(props.value)}
                        maxLength={30}
                        maxWidth={220}
                    />
                ),
                menuTabs: [],
            },
            {
                headerName: 'Date subscribed',
                cellRenderer: (props: ICellRendererParams) => {
                    const createdAt =
                        props.data?.channelSubscriptions[0]?.createdAt;

                    return <>{formatDisplayDate(createdAt, false)}</>;
                },
                menuTabs: [],
                comparator: createSubscriptionDateComparator('createdAt'),
            },
            {
                headerName: 'Date unsubscribed',
                cellRenderer: (props: ICellRendererParams) => {
                    const cancelRequestedAt =
                        props.data?.channelSubscriptions[0]?.cancelRequestedAt;
                    return <>{formatDisplayDate(cancelRequestedAt, false)}</>;
                },
                menuTabs: [],
                hide: subscriberFilter !== ChannelSubscriberKind.Unsubscribed,
                comparator:
                    createSubscriptionDateComparator('cancelRequestedAt'),
            },
            {
                headerName: 'Billing End',
                cellRenderer: (props: ICellRendererParams) => {
                    const cancelAt =
                        props.data?.channelSubscriptions[0]?.cancelAt;
                    return <>{formatDisplayDate(cancelAt, false)}</>;
                },
                menuTabs: [],
                hide:
                    subscriberFilter !== ChannelSubscriberKind.Unsubscribed ||
                    isFreeChannelSubscriptionType,
                comparator: createSubscriptionDateComparator('cancelAt'),
            },
            {
                headerName: 'Expiry',
                cellRenderer: (props: ICellRendererParams) => {
                    const expiryDate =
                        props.data?.channelSubscriptions[0]?.cancelAt;
                    if (expiryDate)
                        return <>{formatDisplayDate(expiryDate, false)}</>;
                    return 'No expiry';
                },
                menuTabs: [],
                hide:
                    ![
                        ChannelSubscriberKind.Expired,
                        ChannelSubscriberKind.NonCharge,
                    ].includes(subscriberFilter) ||
                    isFreeChannelSubscriptionType,
                comparator: createSubscriptionDateComparator('cancelAt'),
            },
            {
                headerName: 'Subscription type',
                menuTabs: [],
                hide: isFreeChannelSubscriptionType,
                cellRenderer: (props: ICellRendererParams) => {
                    const channelSubscription =
                        props.data?.channelSubscriptions[0];
                    const isNonCharge = isNonChargeChannelSubscriptionType(
                        channelSubscription?.subscriptionType
                    );
                    const notes = channelSubscription?.notes;

                    if (isNonCharge && notes) {
                        return (
                            <Tooltip title={notes}>
                                <div>
                                    {formatChannelSubscriptionType(
                                        props.data?.channelSubscriptions[0]
                                    )}
                                </div>
                            </Tooltip>
                        );
                    }

                    return formatChannelSubscriptionType(
                        props.data?.channelSubscriptions[0]
                    );
                },
                comparator: (valueA, valueB, nodeA, nodeB) => {
                    const subscriptionType1 = formatChannelSubscriptionType(
                        nodeA.data?.channelSubscriptions[0]
                    );
                    const subscriptionType2 = formatChannelSubscriptionType(
                        nodeB.data?.channelSubscriptions[0]
                    );
                    return subscriptionType1.localeCompare(subscriptionType2);
                },
            },
            {
                headerName: 'Email notifications',
                cellRenderer: (props: ICellRendererParams) => {
                    const emailNotificationsEnabled =
                        props.data?.channelSubscriptions[0]?.emailNotifications;
                    if (emailNotificationsEnabled)
                        return <UserInterfaceCheck fontSize="medium" />;
                    return null;
                },
                menuTabs: [],
                hide: [
                    ChannelSubscriberKind.Expired,
                    ChannelSubscriberKind.Unsubscribed,
                ].includes(subscriberFilter),
            },
        ],
        [_id, subscriberFilter, channelAdmin, history, data]
    );

    const [accept, reject] = useMemo(() => {
        if (!data?.channel) return [() => {}, () => {}];

        const makeVariables = (userID: string, status: boolean) => ({
            variables: {
                item: {
                    userId: userID,
                    channelId: _id,
                    approved: status,
                },
            },
        });

        if (data.channel.type === ChannelTypes.premium) {
            const onApproveTrialRequestClick = async (userID: string) => {
                const defaultTrialDurationDays =
                    data.channel.defaultTrialDurationDays;
                await processTrialRequest(makeVariables(userID, true));

                setApproveTrialAlertBox({
                    visible: true,
                    channelTrialDuration:
                        defaultTrialDurationDays || config.trialDays,
                });
            };

            const onRejectTrialRequestClick = async (userID: string) => {
                await processTrialRequest(makeVariables(userID, false));

                setRejectTrialAlertBoxVisible(true);
            };

            return [onApproveTrialRequestClick, onRejectTrialRequestClick];
        }

        const accept = async (userID: string) => {
            const res = await processFreeClosedRequest(
                makeVariables(userID, true)
            );
            setAcceptAlertBox({
                visible: true,
                username: res.data.processFreeClosedRequest.name,
            });
        };
        const reject = async (userID: string) => {
            const res = await processFreeClosedRequest(
                makeVariables(userID, false)
            );
            setRejectAlertBox({
                visible: true,
                username: res.data.processFreeClosedRequest.name,
            });
        };

        return [accept, reject];
    }, [processTrialRequest, data, _id]);

    const pendingColumnDefs: ColDef[] = useMemo(
        () => [
            {
                headerName: 'User details',
                field: 'name',
                width: 266,
                menuTabs: [],
                cellRenderer: (props: any) => {
                    const value = formatAnonymousUserData(props.value);
                    return channelAdmin &&
                        canBeDeleted(props.data.channelSubscriptions[0]) ? (
                        <LinkCellRenderer
                            history={history}
                            label={value}
                            url={makeEditSubscriberURL(_id, props.data._id)}
                        />
                    ) : (
                        <ReadOnlyLabelCellRenderer {...props} value={value} />
                    );
                },
            },
            {
                headerName: 'Company',
                field: 'company',
                width: 266,
                cellRenderer: (props: { value: string }) => props.value ?? '',
                menuTabs: [],
            },
            {
                headerName: 'Email',
                field: 'email',
                width: 266,
                cellRenderer: (props: { value: string }) =>
                    formatAnonymousUserData(props.value),
                menuTabs: [],
            },
            {
                headerName: 'Requested on',
                width: 266,
                cellRenderer: (props: ICellRendererParams) => {
                    const createdAt =
                        props.data?.channelSubscriptions[0]?.createdAt;
                    return <>{formatDisplayDateTime(createdAt, false)}</>;
                },
                menuTabs: [],
                comparator: createSubscriptionDateComparator('createdAt'),
            },
            {
                headerName: 'Action',
                width: 120,
                cellRenderer: (props: ICellRendererParams) => (
                    <DecisionCellRenderer
                        onYesClick={() => accept(props.data._id)}
                        onNoClick={() => reject(props.data._id)}
                    />
                ),
                menuTabs: [],
            },
        ],
        [_id, channelAdmin, history, data, accept, reject]
    );

    const trialColumnDefs: ColDef[] = useMemo(
        () => [
            {
                headerName: 'User details',
                field: 'name',
                width: 197,
                menuTabs: [],
                cellRenderer: (props: any) => {
                    const value = formatAnonymousUserData(props.value);
                    return channelAdmin &&
                        canBeDeleted(props.data.channelSubscriptions[0]) ? (
                        <LinkCellRenderer
                            history={history}
                            label={value}
                            url={makeEditSubscriberURL(_id, props.data._id)}
                        />
                    ) : (
                        <ReadOnlyLabelCellRenderer {...props} value={value} />
                    );
                },
            },
            {
                headerName: 'Company',
                field: 'company',
                width: 197,
                cellRenderer: (props: { value: string }) => props.value ?? '',
                menuTabs: [],
            },
            {
                headerName: 'Email',
                field: 'email',
                width: 391,
                cellRenderer: (props: { value: string }) =>
                    formatAnonymousUserData(props.value),
                menuTabs: [],
            },
            {
                headerName: 'Trial started',
                width: 100,
                cellRenderer: (props: ICellRendererParams) => {
                    const updatedAt: Date =
                        props.data?.channelSubscriptions[0]?.updatedAt;
                    return <>{formatDisplayDate(updatedAt, false)}</>;
                },
                menuTabs: [],
                comparator: createSubscriptionDateComparator('updatedAt'),
            },
            {
                headerName: 'Trial ending',
                colId: 'trialEnding',
                width: 100,
                cellRenderer: (props: ICellRendererParams) => {
                    const expiryDate =
                        props.data?.channelSubscriptions[0]?.cancelAt;
                    if (expiryDate)
                        return <>{formatDisplayDate(expiryDate, false)}</>;
                    return 'No expiry';
                },
                menuTabs: [],
                comparator: createSubscriptionDateComparator('cancelAt'),
            },
            {
                headerName: 'Articles read',
                width: 197,
                cellRenderer: (props: ICellRendererParams) => {
                    const viewsCount = props.data?.articleViewsByChannel || 0;
                    if (viewsCount) {
                        return formatPlural(viewsCount, 'article');
                    }
                    return 'No articles';
                },
                menuTabs: [],
            },
        ],
        [_id, channelAdmin, history]
    );

    const defaultSortingCol = useMemo(() => {
        if (!gridApi) return;
        switch (subscriberFilter) {
            case ChannelSubscriberKind.Trial:
                return 'trialEnding';
            default:
                return;
        }
    }, [gridApi, subscriberFilter]);

    useEffect(() => {
        if (!defaultSortingCol || !gridApi) return;

        const state = gridApi.getColumnState();
        if (!state) return;
        gridApi.applyColumnState({
            state: state.map((cs) =>
                cs.colId === defaultSortingCol ? { ...cs, sort: 'asc' } : cs
            ),
        });
    }, [defaultSortingCol, gridApi]);

    if (
        !data ||
        loading ||
        processTrialRequestLoading ||
        processFreeClosedRequestLoading ||
        (!isInsightsAdmin &&
            isUserNotChannelAdmin(data.channel.adminUsers, neonUserId))
    ) {
        return <Loader />;
    }

    const channel = data.channel;

    const subscribedUsers = channel.channelSubscribedUsers.filter((user) => {
        const subscription = user.channelSubscriptions?.[0];

        switch (subscriberFilter) {
            case ChannelSubscriberKind.Expired:
                return subscription.subscriptionState === 'expired';
            case ChannelSubscriberKind.NonCharge:
                return (
                    !isSubscriptionExpired(subscription) &&
                    subscription.subscriptionState === 'active' &&
                    (isNonChargeChannelSubscriptionType(
                        subscription.subscriptionType
                    ) ||
                        (isAccountChannelSubscriptionType(
                            subscription.subscriptionType
                        ) &&
                            subscription.rebate))
                );
            case ChannelSubscriberKind.Paid:
                return isActivePaidSubscription(subscription);
            case ChannelSubscriberKind.Unsubscribed:
                return (
                    subscription.subscriptionState === 'canceled' ||
                    ((isPaidSubscription(subscription) ||
                        isRebatedAccountSubscription(subscription)) &&
                        !isSubscriptionActive(subscription) &&
                        subscription.subscriptionState !== 'expired')
                );
            case ChannelSubscriberKind.PaymentFailed:
                return subscription.subscriptionState === 'paymentFailed';
            case ChannelSubscriberKind.Trial:
                return isTrialChannelSubscriptionType(
                    subscription.subscriptionType
                );
            case ChannelSubscriberKind.All:
            default:
                return isSubscriptionActive(subscription);
        }
    });

    const pendingSubscription = (cs: ChannelSubscription) =>
        cs.channelId === channel._id && cs.subscriptionState === 'pending';
    const pendingTrialSubscription = (cs: ChannelSubscription) =>
        pendingSubscription(cs) &&
        cs.subscriptionType === ChannelSubscriptionType.Trial;

    const isPendingUser = (user: User) =>
        user.channelSubscriptions.some(pendingSubscription);
    const isPendingTrialUser = (user: User) =>
        user.channelSubscriptions.some(pendingTrialSubscription);

    const sortBySubscriptionDateField =
        (field: 'createdAt' | 'cancelAt') => (a: User, b: User) => {
            const acs: ChannelSubscription | undefined =
                a.channelSubscriptions.find(pendingTrialSubscription);
            const bcs: ChannelSubscription | undefined =
                b.channelSubscriptions.find(pendingTrialSubscription);
            return (
                new Date(acs?.[field] ?? new Date()).getTime() -
                new Date(bcs?.[field] ?? new Date()).getTime()
            );
        };

    const sortByTrialRequestedAtAsc = sortBySubscriptionDateField('createdAt');
    const sortByTrialEndingAsc = sortBySubscriptionDateField('cancelAt');

    const pendingTrialUsers = subscribedUsers
        .filter(isPendingTrialUser)
        .sort(sortByTrialRequestedAtAsc);
    const activeTrialUsers = subscribedUsers
        .filter(isActiveTrialUser)
        .sort(sortByTrialEndingAsc)
        .map((u) => ({
            ...u,
            articleViewsByChannel: activeTrialUsersWithViewCount[u._id] || 0,
        }));

    const pendingUsers = channel.channelSubscribedUsers
        .filter(isPendingUser)
        .sort(sortByTrialRequestedAtAsc);

    const toolbar = (
        <ChannelSubscribersFilter
            channelType={channel.type}
            channelSubscriberKind={subscriberFilter}
            onQueryStringChange={setQueryString}
            onSelectedKindChange={setSubscriberFilter}
            searchString={queryString}
            onAddNewSubscriber={(): void => {
                history.push(
                    baseUrls.publisher.premiumChannels.addSubscriber.replace(
                        ':_id',
                        channel._id
                    )
                );
            }}
            onAddBulkNewSubscribers={(): void => {
                history.push(
                    baseUrls.publisher.premiumChannels.bulkAddSubscribers.replace(
                        ':_id',
                        channel._id
                    )
                );
            }}
        />
    );

    const gridOptions = {
        onNewColumnsLoaded: (e: NewColumnsLoadedEvent) =>
            e.api.sizeColumnsToFit(),
    };

    const isTrial = subscriberFilter === ChannelSubscriberKind.Trial;
    const isFreeClosedAll =
        subscriberFilter === ChannelSubscriberKind.All &&
        channel.type === ChannelTypes.freeClosed;
    return (
        <ContainerBox sx={{ maxWidth: 1200, mx: 'auto', my: 2 }}>
            <Grid container spacing={2}>
                <Snackbar
                    message={`The user is now on a ${approveTrialAlertBox.channelTrialDuration}-day trial period, and they’ve been notified via email.`}
                    type={MessageTypes.info}
                    open={approveTrialAlertBox.visible}
                    onClose={() => setApproveTrialAlertBox({ visible: false })}
                />
                <Snackbar
                    message="The trial request has been rejected, and the user has been notified via email."
                    type={MessageTypes.info}
                    open={rejectTrialAlertBoxVisible}
                    onClose={() => setRejectTrialAlertBoxVisible(false)}
                />
                <Snackbar
                    message={`${acceptAlertBox.username} is now a subscriber`}
                    type={MessageTypes.info}
                    open={acceptAlertBox.visible}
                    onClose={() =>
                        setAcceptAlertBox({ visible: false, username: '' })
                    }
                />
                <Snackbar
                    message={`${rejectAlertBox.username} won't get access to your channel`}
                    type={MessageTypes.info}
                    open={rejectAlertBox.visible}
                    onClose={() =>
                        setRejectAlertBox({ visible: false, username: '' })
                    }
                />
                <Grid size={12}>
                    <SubscriberCategoriesStatsBreadcrumbs channel={channel} />
                </Grid>
                <Grid size={12}>
                    <Typography variant="h5">{channel.name}</Typography>
                </Grid>
                <Grid size={12}>
                    <ChannelLabel type={channel.type} withDescription />
                </Grid>
                <Grid size={12}>
                    <SubscriberStatsPanel
                        channel={channel}
                        channelSubscriberKind={subscriberFilter}
                    />
                </Grid>
                {isTrial && (
                    <Grid size={12}>
                        <AdminList
                            listPageHeader={true}
                            aboveTable={
                                pendingTrialUsers.length > 0 ? (
                                    <Typography variant="h6" sx={{ mt: 2 }}>
                                        Pending requests
                                    </Typography>
                                ) : null
                            }
                            showHeader={false}
                            name={channel.name}
                            items={pendingTrialUsers}
                            loading={loading}
                            columns={pendingColumnDefs}
                            showAdd={false}
                            apiCallback={gridCallback}
                            showCaption={false}
                            pagination={false}
                            gridOptions={gridOptions}
                            overrideContainerSx={{ mb: 2 }}
                            overrideListSx={{ minHeight: 2 }}
                            showTableWhenEmpty={false}
                            toolbar={toolbar}
                        />
                        <Typography variant="h6">
                            Users currently on a trial period
                        </Typography>
                        <AdminList
                            listPageHeader={true}
                            entityForCaptionText={handleEntityPluralCase(
                                channel.channelSubscribedUsers?.length,
                                'user'
                            )}
                            showHeader={false}
                            name={channel.name}
                            items={activeTrialUsers}
                            loading={loading}
                            columns={trialColumnDefs}
                            showAdd={false}
                            apiCallback={gridCallback}
                            overrideListSx={{ minHeight: 2 }}
                            gridOptions={gridOptions}
                        />
                    </Grid>
                )}
                {isFreeClosedAll && (
                    <Grid size={12}>
                        <AdminList
                            listPageHeader={true}
                            aboveTable={
                                pendingUsers.length > 0 ? (
                                    <Typography variant="h6" sx={{ mt: 2 }}>
                                        Pending requests
                                    </Typography>
                                ) : null
                            }
                            showHeader={false}
                            name={channel.name}
                            items={pendingUsers}
                            loading={loading}
                            columns={pendingColumnDefs}
                            showAdd={false}
                            apiCallback={gridCallback}
                            showCaption={false}
                            pagination={false}
                            gridOptions={gridOptions}
                            overrideContainerSx={{ mb: 2 }}
                            overrideListSx={{ minHeight: 2 }}
                            showTableWhenEmpty={false}
                            toolbar={toolbar}
                        />
                        <Typography variant="h6">Active subscribers</Typography>
                        <AdminList
                            listPageHeader={true}
                            entityForCaptionText={handleEntityPluralCase(
                                channel.channelSubscribedUsers?.length,
                                'user'
                            )}
                            showHeader={false}
                            name={channel.name}
                            items={subscribedUsers}
                            loading={loading}
                            columns={columnDefs}
                            showAdd={false}
                            apiCallback={gridCallback}
                            overrideListSx={{ minHeight: 2 }}
                            gridOptions={gridOptions}
                        />
                    </Grid>
                )}
                {!isTrial && !isFreeClosedAll && (
                    <Grid size={12}>
                        <AdminList
                            entityForCaptionText={handleEntityPluralCase(
                                channel.channelSubscribedUsers?.length,
                                'user'
                            )}
                            name={channel.name}
                            showHeader={false}
                            items={subscribedUsers}
                            loading={loading}
                            columns={columnDefs}
                            showAdd={false}
                            apiCallback={gridCallback}
                            gridOptions={gridOptions}
                            toolbar={toolbar}
                        />
                    </Grid>
                )}
            </Grid>
        </ContainerBox>
    );
};

export default ChannelsStatsPage;
